/**
 * useQuestionTimer - shared countdown for question panels.
 *
 * Every panel type (action, grid, multiselect, form) counts down the same
 * `timeout_seconds` field, so the countdown lives here instead of being
 * reimplemented per component.
 */

import { useEffect, useState } from 'react'

/** Countdown progress, tagged with the reset key it was produced for. */
interface TimerTick {
  key: string
  timeLeft: number
  timedOut: boolean
}

export interface UseQuestionTimerResult {
  /** Seconds remaining, or null when the panel has no timeout. */
  timeLeft: number | null
  /** True once the countdown reached zero for the current reset key. */
  timedOut: boolean
}

/**
 * Counts down a panel timeout.
 *
 * The deadline is computed once per reset (not re-derived per tick), so a
 * delayed tick cannot stretch the countdown.
 *
 * @param timeoutSeconds Panel timeout. `0`, `undefined` or `null` mean "no timer".
 * @param resetKey Restarts the countdown when it changes. Pass the request ID —
 *   panel IDs are reused across rounds by game modes.
 */
export function useQuestionTimer(
  timeoutSeconds: number | null | undefined,
  resetKey: string
): UseQuestionTimerResult {
  // Ticks are written only from the interval callback, never synchronously from the
  // effect body. The reset is handled by deriving below instead of clearing state,
  // so a tick belonging to a previous reset key is simply ignored.
  const [tick, setTick] = useState<TimerTick | null>(null)

  useEffect(() => {
    if (!timeoutSeconds) return

    const endAt = Date.now() + timeoutSeconds * 1000
    // Holder, because a tick may need to stop the interval before setInterval() returns.
    const handle: { id?: ReturnType<typeof setInterval> } = {}

    const onTick = () => {
      const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000))
      setTick({ key: resetKey, timeLeft: remaining, timedOut: remaining === 0 })
      if (remaining === 0 && handle.id !== undefined) {
        // Nothing left to count — stop ticking until the next reset.
        clearInterval(handle.id)
      }
    }

    handle.id = setInterval(onTick, 1000)
    return () => {
      if (handle.id !== undefined) clearInterval(handle.id)
    }
  }, [timeoutSeconds, resetKey])

  if (!timeoutSeconds) {
    return { timeLeft: null, timedOut: false }
  }
  // Until the first tick for this reset key arrives, show the full timeout.
  if (tick === null || tick.key !== resetKey) {
    return { timeLeft: timeoutSeconds, timedOut: false }
  }
  return { timeLeft: tick.timeLeft, timedOut: tick.timedOut }
}
